import type { QueryClient } from "@tanstack/react-query";
import { lowerFirst } from "lodash";
import {
    EVENT_DEFAULT_INSTANCE,
    type FormValues,
    RELATION_DEFAULT_INSTANCE,
    type SubmitMeta,
} from "@/widgets/classic-form/form";
import { getEventModelName } from "@/entities/data-model";
import {
    type EventFieldsGroup,
    type FormMetadataResponse,
    GroupType,
    type RelationshipFieldGroup,
    ResponseItemType,
} from "@/entities/form-metadata";
import type { Person } from "@/entities/tree-person";
import { getPersonOptions } from "@/entities/tree-person";
import type { FormAPI } from "@/shared/forms";
import { buildResetFormValues } from "./buildResetFormValues";
import { transformPersonToFormValues } from "./transformPerson";

/**
 * Ensures field groups that should be visible have at least one default instance.
 * When quickfill data lacks values for a group (e.g., no Death event, no Father),
 * the group would otherwise end up with an empty instances array and disappear from the form.
 *
 * Mirrors the metadata converter logic: groups are preserved when they are
 * non-removable OR prefilled (`fieldsetDisplayStrategy !== "empty-dynamic"`).
 */
export const ensureNonRemovableDefaults = (values: FormValues, formMetadata: FormMetadataResponse): void => {
    for (const item of formMetadata.fieldGroupsAndFeatureContainers) {
        if (item.type !== ResponseItemType.FieldGroup) continue;

        if (item.groupType === GroupType.Event) {
            const eventGroup = item as EventFieldsGroup;
            // Skip groups with "empty-dynamic" or "prefilled-dynamic" strategy —
            // empty-dynamic were never shown; prefilled-dynamic had empty defaults intentionally stripped.
            if (eventGroup.fieldsetDisplayStrategy === "empty-dynamic") continue;
            if (eventGroup.fieldsetDisplayStrategy === "prefilled-dynamic") continue;

            for (const fieldset of eventGroup.fieldsets) {
                const key = getEventModelName(fieldset);
                if (key && values.events[key as keyof FormValues["events"]]?.instances.length === 0) {
                    values.events[key as keyof FormValues["events"]]!.instances = [EVENT_DEFAULT_INSTANCE];
                }
            }
        } else if (item.groupType === GroupType.FamilyMember) {
            const familyGroup = item as RelationshipFieldGroup;
            // Same logic: skip empty-dynamic and prefilled-dynamic
            if (familyGroup.fieldsetDisplayStrategy === "empty-dynamic") continue;
            if (familyGroup.fieldsetDisplayStrategy === "prefilled-dynamic") continue;

            for (const fieldset of familyGroup.fieldsets) {
                const key = lowerFirst(fieldset.relation) as keyof FormValues["family"];
                if (values.family[key]?.instances.length === 0) {
                    values.family[key].instances = [RELATION_DEFAULT_INSTANCE];
                }
            }
        }
    }
};

export interface ApplyPersonToFormArgs {
    personId: string;
    treeId: string;
    queryClient: QueryClient;
    formApi: FormAPI<FormValues, SubmitMeta>;
    formMetadata: FormMetadataResponse;
    defaultFormValues: FormValues;
}

/**
 * The quick-fill "apply person" core: fetches the person (cache-first), builds a
 * reset base from the default values, transforms the person into form values and
 * applies them to the mounted form as a DRAFT via `formApi.setValues`.
 *
 * Deliberately takes `formApi` and its sibling deps as arguments so it can be
 * called both from inside the `<Form>` tree (useQuickFillSelection) and from
 * outside it (ClassicForm's imperative `applyPerson` handle, where `useFormAPI`
 * is not available).
 *
 * Does not announce or handle errors — callers own those concerns.
 */
export const applyPersonToForm = async ({
    personId,
    treeId,
    queryClient,
    formApi,
    formMetadata,
    defaultFormValues,
}: ApplyPersonToFormArgs): Promise<Person> => {
    const person = await queryClient.ensureQueryData(getPersonOptions({ personId, treeId }));

    const currentValues = formApi.getValues();
    const base = buildResetFormValues(defaultFormValues, currentValues, { preserveName: false });

    const transformed = transformPersonToFormValues(person, base, formMetadata, currentValues);

    ensureNonRemovableDefaults(transformed, formMetadata);

    formApi.setValues(transformed);
    return person;
};
